import { Spinner } from '@/components/ui/spinner';
import { FetchSiteDevicesQuery } from '@/lib/api/sites/devices';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { VideoStreamType } from '@/lib/api/types.gen';
import { FetchFullStreamQuery } from '@/lib/api/monitoring/alert';
import { useCurrentTime } from '@/lib/time';
import { tracking } from '@/lib/tracking/tracking-emitter';
import { LiveBadge } from '@/app/Alert/AlertLiveViewSidecar/AlertLiveViewSidecar';
import { VideoPlayerCompat } from '@/app/Alert/AlertEventPlayback/components/VideoPlayer';
import { LiveCameraSelect } from '@/app/Alert/components/LiveCameraSelect';

export type LivePlayerProps = {
  companyId: string;
  projectId: string;
  isLiveUrlLoading: boolean;
  liveUrl?: string;
  liveUrlType?: VideoStreamType;
  videoProcessorIdentifier?: string;
};

export const LivePlayer = ({ companyId, projectId, videoProcessorIdentifier, ...props }: LivePlayerProps) => {
  const [cameraOverrideId, setCameraOverrideId] = useState<string>();
  
  const devicesQuery = useQuery(FetchSiteDevicesQuery({ companyId, projectId }));
  const devices = devicesQuery.data?.devices ?? [];
  const liveCameraOptions = useMemo(
    () =>
      devices
        .filter((d) => d.secondaryDevice?.id)
        .map(({ secondaryDevice }) => ({
          id: secondaryDevice?.id ?? '',
          name: secondaryDevice?.name ?? '',
          monitored: secondaryDevice?.monitored ?? false,
        }))
        .sort((a, b) => {
          if (a.monitored !== b.monitored) return b.monitored ? 1 : -1;
          return a.name.localeCompare(b.name);
        }) ?? [],
    [devices]
  );
  const activeLiveStream = useRef<{
    cameraId: string;
    cameraName: string;
    startedAt: Date;
  }>();
  const cameraOverride = liveCameraOptions.find(
    (x) => cameraOverrideId != null && x.id === cameraOverrideId
  );
  const now = useCurrentTime(60 * 5 * 1000); // some sort of stable time ref
  const overrideLiveStreamQuery = useQuery({
    ...FetchFullStreamQuery({
      companyId,
      projectId,
      occurred: now,
      videoProcessorIdentifier: cameraOverride?.id ?? '',
    }),
    enabled: cameraOverride?.id != null,
  });
  const config =
    cameraOverrideId != null
      ? {
          isLoading: overrideLiveStreamQuery.isLoading,
          liveUrl: overrideLiveStreamQuery.data?.liveUrl,
          urlType: overrideLiveStreamQuery.data?.liveUrlType,
        }
      : {
          isLoading: props.isLiveUrlLoading,
          liveUrl: props.liveUrl,
          urlType: props.liveUrlType,
        };

  useEffect(() => {
    return () => {
      if (!activeLiveStream.current) {
        console.debug('No active livestream.');
        return;
      }

      if (cameraOverride != null && cameraOverride.id != activeLiveStream.current.cameraId) {
        // Cameras dont match, exit
        console.debug(
          'Cameras dont match, not tracking feed.',
          cameraOverride.name,
          activeLiveStream.current.cameraName
        );
        return;
      }

      tracking.track({
        type: 'alert.live_view.stop',
        cameraName: activeLiveStream.current.cameraName,
        start: activeLiveStream.current.startedAt,
        end: new Date(),
      });
      activeLiveStream.current = undefined;
    };
  }, [cameraOverrideId, activeLiveStream]);

  const onLiveStreamLoad = () => {
    const selectedEventCam = liveCameraOptions.find(
      (x) => x.id === videoProcessorIdentifier
    );

    const cam = cameraOverride ?? selectedEventCam;
    if (!cam) {
      console.debug('No cam.');
      return;
    }

    if (cam.id === activeLiveStream.current?.cameraId) return;

    console.debug('Live stream started', cam);

    tracking.track({
      type: 'alert.live_view.start',
      cameraName: cam.name,
    });

    activeLiveStream.current = {
      cameraId: cam.id,
      cameraName: cam.name,
      startedAt: new Date(),
    };
  };

  return (
    <div className="group relative w-full">
      {config.isLoading ? (
        <div className="flex h-full w-full items-center justify-center gap-4">
          <p className="text-lg font-bold text-gray-500">Connecting To Camera</p>
          <Spinner size={'large'} className="text-gray-500" />
        </div>
      ) : (
        <>
          <LiveBadge className="left-4" />
          <VideoPlayerCompat
            url={config.liveUrl ?? undefined}
            urlType={config.urlType}
            onLoad={onLiveStreamLoad}
          />
        </>
      )}
      <div className="absolute right-0 top-0 p-2 opacity-50 transition-opacity duration-100 group-hover:opacity-100">
        <LiveCameraSelect
          options={liveCameraOptions}
          onChange={setCameraOverrideId}
          value={cameraOverrideId}
        />
      </div>
    </div>
  );
};
